import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Building2, Search, ShieldCheck, CheckCircle, ArrowRight, Link2, ChevronsUpDown, Check, AlertCircle } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import type { HealthcareProvider, InsertHealthcareProvider, HospitalPartner } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useHealthcareProviders, QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertHealthcareProviderSchema } from "@shared/schema";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const PROVIDER_TYPES = [
  { value: "hospital", label: "Hospital" },
  { value: "clinic", label: "Clinic" },
  { value: "health_center", label: "Health Center" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "lab", label: "Lab" },
  { value: "specialist", label: "Specialist" },
  { value: "other", label: "Other" },
];

export function ProviderManagement() {
  const { toast } = useToast();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<HealthcareProvider | null>(null);
  const [deletingProviderId, setDeletingProviderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [providerComboOpen, setProviderComboOpen] = useState(false);
  const [selectedExistingProvider, setSelectedExistingProvider] = useState<HealthcareProvider | null>(null);
  const itemsPerPage = 10;

  const { data: providers = [], isLoading } = useHealthcareProviders();

  const { data: hospitalPartners = [] } = useQuery<HospitalPartner[]>({
    queryKey: ["/api/admin/hospital-partners"],
  });

  const getPartnerForProvider = (provider: HealthcareProvider): HospitalPartner | undefined => {
    return hospitalPartners.find(
      (p) => p.healthcareProviderId === provider.id || p.facilityId === provider.id
    );
  };

  const defaultFormValues: Partial<InsertHealthcareProvider> = {
    name: "",
    providerType: "hospital",
    address: "",
    city: "",
    state: "MN",
    zipCode: "",
    phone: "",
    email: "",
    website: "",
    notes: null,
    lat: null,
    lng: null,
    paymentMethod: "check",
    taxId: "",
    bankName: "",
    bankRoutingNumber: "",
    bankAccountNumber: "",
    payableTo: "",
    verificationStatus: "pending",
    isActive: true,
  };

  const addForm = useForm<InsertHealthcareProvider>({
    mode: 'onBlur',
    resolver: zodResolver(insertHealthcareProviderSchema),
    defaultValues: defaultFormValues,
  });

  const editForm = useForm<InsertHealthcareProvider>({
    mode: 'onBlur',
    resolver: zodResolver(insertHealthcareProviderSchema),
  });

  const createMutation = useMutation({
    mutationFn: async (data: InsertHealthcareProvider) => {
      const res = await apiRequest("POST", "/api/healthcare-providers", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.healthcareProviders });
      setIsAddDialogOpen(false);
      setSelectedExistingProvider(null);
      addForm.reset(defaultFormValues);
      toast({
        title: "Provider added",
        description: "The healthcare provider has been added successfully.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InsertHealthcareProvider> }) => {
      const res = await apiRequest("PATCH", `/api/healthcare-providers/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.healthcareProviders });
      setEditingProvider(null);
      toast({
        title: "Provider updated",
        description: "The healthcare provider has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/healthcare-providers/${id}`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.healthcareProviders });
      setDeletingProviderId(null);
      toast({
        title: "Provider archived",
        description: "The healthcare provider has been archived successfully.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/healthcare-providers/${id}/verify`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.healthcareProviders });
      toast({
        title: "Provider verified",
        description: "The healthcare provider has been verified for payment processing.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleExistingProviderSelect = (providerId: string) => {
    const provider = providers.find(p => p.id === providerId);
    if (provider) {
      setSelectedExistingProvider(provider);
      addForm.setValue("name", provider.name);
      addForm.setValue("providerType", provider.providerType);
      addForm.setValue("address", provider.address || "");
      addForm.setValue("city", provider.city || "");
      addForm.setValue("state", provider.state || "MN");
      addForm.setValue("zipCode", provider.zipCode || "");
      addForm.setValue("phone", provider.phone || "");
      addForm.setValue("email", provider.email || "");
      addForm.setValue("website", provider.website || "");
      addForm.setValue("notes", provider.notes || null);
      addForm.setValue("lat", provider.lat || null);
      addForm.setValue("lng", provider.lng || null);
    }
    setProviderComboOpen(false);
  };

  const handleClearSelection = () => {
    setSelectedExistingProvider(null);
    addForm.reset(defaultFormValues);
  };

  const handleEdit = (provider: HealthcareProvider) => {
    setEditingProvider(provider);
    editForm.reset({
      name: provider.name,
      providerType: provider.providerType,
      address: provider.address || "",
      city: provider.city || "",
      state: provider.state || "MN",
      zipCode: provider.zipCode || "",
      phone: provider.phone || "",
      email: provider.email || "",
      website: provider.website || "",
      notes: provider.notes,
      lat: provider.lat,
      lng: provider.lng,
      paymentMethod: provider.paymentMethod || "check",
      taxId: provider.taxId || "",
      bankName: provider.bankName || "",
      bankRoutingNumber: provider.bankRoutingNumber || "",
      bankAccountNumber: provider.bankAccountNumber || "",
      payableTo: provider.payableTo || "",
      verificationStatus: provider.verificationStatus,
      isActive: provider.isActive ?? true,
    });
  };

  const filteredProviders = providers.filter((provider) => {
    if (typeFilter !== "all" && provider.providerType !== typeFilter) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      provider.name.toLowerCase().includes(query) ||
      (provider.city || "").toLowerCase().includes(query) ||
      (provider.address || "").toLowerCase().includes(query)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredProviders.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProviders = filteredProviders.slice(startIndex, startIndex + itemsPerPage);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  const renderProviderFormFields = (form: ReturnType<typeof useForm<InsertHealthcareProvider>>) => (
    <>
      <FormField
        control={form.control}
        name="providerType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Type</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger data-testid="select-provider-type">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {PROVIDER_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Phone</FormLabel>
            <FormControl>
              <Input {...field} value={field.value || ""} data-testid="input-provider-phone" placeholder="(612) 555-0100" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>Address</FormLabel>
            <FormControl>
              <Input {...field} value={field.value || ""} data-testid="input-provider-address" placeholder="701 Park Ave" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem>
            <FormLabel>City</FormLabel>
            <FormControl>
              <Input {...field} value={field.value || ""} data-testid="input-provider-city" placeholder="Minneapolis" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex gap-4">
        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>State</FormLabel>
              <FormControl>
                <Input {...field} value={field.value || ""} data-testid="input-provider-state" placeholder="MN" maxLength={2} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="zipCode"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>ZIP</FormLabel>
              <FormControl>
                <Input {...field} value={field.value || ""} data-testid="input-provider-zip" placeholder="55415" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input {...field} value={field.value || ""} data-testid="input-provider-email" placeholder="contact@provider.org" type="email" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="website"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Website</FormLabel>
            <FormControl>
              <Input {...field} value={field.value || ""} data-testid="input-provider-website" placeholder="https://provider.org" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>Notes</FormLabel>
            <FormControl>
              <Textarea {...field} value={field.value || ""} data-testid="input-provider-notes" placeholder="Internal notes about this provider..." className="resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );

  const renderAddForm = () => (
    <Form {...addForm}>
      <form onSubmit={addForm.handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
        <div className="space-y-3">
          <FormLabel>Select Existing Provider</FormLabel>
          <div className="flex gap-2 items-start">
            <Popover open={providerComboOpen} onOpenChange={setProviderComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={providerComboOpen}
                  className="w-full justify-between font-normal"
                  data-testid="select-existing-provider"
                  type="button"
                >
                  {selectedExistingProvider
                    ? selectedExistingProvider.name
                    : "Search providers in database..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[460px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by name, city, or type..." data-testid="input-search-existing-provider" />
                  <CommandList>
                    <CommandEmpty>No providers found in database.</CommandEmpty>
                    <CommandGroup heading="Hospitals">
                      {providers.filter(p => p.providerType === "hospital").map((p) => (
                        <CommandItem
                          key={p.id}
                          value={`${p.name} ${p.city || ""} ${p.providerType}`}
                          onSelect={() => handleExistingProviderSelect(p.id)}
                          data-testid={`option-existing-provider-${p.id}`}
                        >
                          <Check className={cn("mr-2 h-4 w-4", selectedExistingProvider?.id === p.id ? "opacity-100" : "opacity-0")} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{p.name}</div>
                            {p.city && p.state && (
                              <div className="text-xs text-muted-foreground">{p.city}, {p.state}</div>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="Clinics">
                      {providers.filter(p => p.providerType === "clinic").map((p) => (
                        <CommandItem
                          key={p.id}
                          value={`${p.name} ${p.city || ""} ${p.providerType}`}
                          onSelect={() => handleExistingProviderSelect(p.id)}
                          data-testid={`option-existing-provider-${p.id}`}
                        >
                          <Check className={cn("mr-2 h-4 w-4", selectedExistingProvider?.id === p.id ? "opacity-100" : "opacity-0")} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{p.name}</div>
                            {p.city && p.state && (
                              <div className="text-xs text-muted-foreground">{p.city}, {p.state}</div>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="Health Centers">
                      {providers.filter(p => p.providerType === "health_center").map((p) => (
                        <CommandItem
                          key={p.id}
                          value={`${p.name} ${p.city || ""} ${p.providerType}`}
                          onSelect={() => handleExistingProviderSelect(p.id)}
                          data-testid={`option-existing-provider-${p.id}`}
                        >
                          <Check className={cn("mr-2 h-4 w-4", selectedExistingProvider?.id === p.id ? "opacity-100" : "opacity-0")} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{p.name}</div>
                            {p.city && p.state && (
                              <div className="text-xs text-muted-foreground">{p.city}, {p.state}</div>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="Pharmacies, Labs & Other">
                      {providers.filter(p => !["hospital", "clinic", "health_center"].includes(p.providerType)).map((p) => (
                        <CommandItem
                          key={p.id}
                          value={`${p.name} ${p.city || ""} ${p.providerType}`}
                          onSelect={() => handleExistingProviderSelect(p.id)}
                          data-testid={`option-existing-provider-${p.id}`}
                        >
                          <Check className={cn("mr-2 h-4 w-4", selectedExistingProvider?.id === p.id ? "opacity-100" : "opacity-0")} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{p.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {PROVIDER_TYPES.find(t => t.value === p.providerType)?.label || p.providerType}
                              {p.city && p.state ? ` · ${p.city}, ${p.state}` : ""}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedExistingProvider && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClearSelection}
                title="Clear selection"
                data-testid="button-clear-provider-selection"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          {selectedExistingProvider && (
            <div className="flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950 p-2.5 text-xs" data-testid="info-provider-selected">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <span className="text-blue-800 dark:text-blue-300">
                Form auto-filled from <strong>{selectedExistingProvider.name}</strong>. This will create a duplicate record. If the provider already exists, consider editing it instead.
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={addForm.control}
            name="name"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Provider Name</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="input-provider-name" placeholder="e.g., Hennepin Healthcare" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {renderProviderFormFields(addForm)}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { setIsAddDialogOpen(false); setSelectedExistingProvider(null); addForm.reset(defaultFormValues); }}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-provider">
            {createMutation.isPending ? "Saving..." : "Add Provider"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );

  const renderEditForm = () => (
    <Form {...editForm}>
      <form onSubmit={editForm.handleSubmit((data) => editingProvider && updateMutation.mutate({ id: editingProvider.id, data }))} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={editForm.control}
            name="name"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Provider Name</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="input-provider-name" placeholder="e.g., Hennepin Healthcare" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {renderProviderFormFields(editForm)}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setEditingProvider(null)}>
            Cancel
          </Button>
          <Button type="submit" disabled={updateMutation.isPending} data-testid="button-submit-provider">
            {updateMutation.isPending ? "Saving..." : "Update Provider"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading providers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-semibold" data-testid="text-provider-heading">Provider Management</h2>
          <p className="text-muted-foreground mt-1">Manage all healthcare providers participating on the platform</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) {
            setSelectedExistingProvider(null);
            addForm.reset(defaultFormValues);
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-provider">
              <Plus className="h-4 w-4 mr-2" />
              Add Provider
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Healthcare Provider</DialogTitle>
              <DialogDescription>Select an existing provider from the database to auto-fill, or enter details manually</DialogDescription>
            </DialogHeader>
            {renderAddForm()}
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <CardTitle>All Providers ({filteredProviders.length})</CardTitle>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, city, or address..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
                data-testid="input-search-providers"
              />
            </div>
            <Tabs value={typeFilter} onValueChange={handleTypeFilterChange}>
              <TabsList>
                <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                <TabsTrigger value="hospital" data-testid="filter-hospital">Hospitals</TabsTrigger>
                <TabsTrigger value="clinic" data-testid="filter-clinic">Clinics</TabsTrigger>
                <TabsTrigger value="health_center" data-testid="filter-health-center">Health Centers</TabsTrigger>
                <TabsTrigger value="pharmacy" data-testid="filter-pharmacy">Pharmacies</TabsTrigger>
                <TabsTrigger value="lab" data-testid="filter-lab">Labs</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedProviders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No providers found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedProviders.map((provider) => {
                  const partner = getPartnerForProvider(provider);
                  return (
                  <TableRow key={provider.id} data-testid={`row-provider-${provider.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{provider.name}</span>
                        {partner && (
                          <Badge variant="default" data-testid={`badge-partner-${provider.id}`}>
                            <Link2 className="h-3 w-3 mr-1" />
                            Partner
                          </Badge>
                        )}
                      </div>
                      {provider.email && (
                        <div className="text-xs text-muted-foreground">{provider.email}</div>
                      )}
                      {partner && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Code: <span className="font-mono">{partner.partnerCode}</span>
                          {" · "}
                          <Badge variant={partner.status === "active" ? "default" : "secondary"}>
                            {partner.status}
                          </Badge>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{provider.providerType}</Badge>
                    </TableCell>
                    <TableCell>
                      {provider.city && provider.state ? (
                        <div>
                          <div>{provider.city}, {provider.state}</div>
                          {provider.address && (
                            <div className="text-xs text-muted-foreground">{provider.address}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {provider.verificationStatus === "verified" ? (
                        <Badge variant="default">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {provider.verificationStatus !== "verified" && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => verifyMutation.mutate(provider.id)}
                            disabled={verifyMutation.isPending}
                            title="Verify provider"
                            data-testid={`button-verify-provider-${provider.id}`}
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleEdit(provider)}
                          data-testid={`button-edit-provider-${provider.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeletingProviderId(provider.id)}
                          data-testid={`button-delete-provider-${provider.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                        {partner && (
                          <Link href={`/partner-portal/${partner.id}`}>
                            <Button
                              size="icon"
                              variant="ghost"
                              title="View partner portal"
                              data-testid={`button-portal-provider-${provider.id}`}
                            >
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {filteredProviders.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between gap-4 mt-4">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({filteredProviders.length} providers)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  data-testid="button-prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  data-testid="button-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editingProvider !== null} onOpenChange={(open) => !open && setEditingProvider(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Provider</DialogTitle>
            <DialogDescription>Update healthcare provider information</DialogDescription>
          </DialogHeader>
          {editingProvider && renderEditForm()}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deletingProviderId !== null} onOpenChange={(open) => !open && setDeletingProviderId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this provider?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive the healthcare provider. They will no longer appear in searches or be available for new transactions.
              Existing wallet transactions and payment records referencing this provider will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingProviderId && deleteMutation.mutate(deletingProviderId)}
              className="bg-destructive"
              data-testid="button-confirm-delete-provider"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}